// External dependencies
import React, { useEffect, useState } from 'react';

// Internal dependencies
import AdminLayout from '@/modules/admin/client/components/AdminLayout.component';
import CmsAdminNav from '@/modules/cms/client/components/CmsAdminNav.component';
import { getPages, updatePage } from '@/modules/cms/client/api/admin-cms.api';

/**
 * Sort pages the way the public menu endpoint does, so the admin ordering
 * preview matches what visitors see.
 *
 * @param {Array} pages - Pages to sort
 * @returns {Array} New sorted array
 */
function sortForMenu(pages) {
  return [...pages].sort((a, b) => {
    if ((a.menuOrder || 0) !== (b.menuOrder || 0)) {
      return (a.menuOrder || 0) - (b.menuOrder || 0);
    }
    return (a.title || '').localeCompare(b.title || '');
  });
}

export default function AdminCmsMenus() {
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    setIsLoading(true);
    setError(null);

    getPages({ status: 'published' })
      .then(data => setPages(sortForMenu(data)))
      .catch(() => setError('Failed to load pages.'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const persist = async (id, payload) => {
    try {
      await updatePage(id, payload);
    } catch (err) {
      setError('Failed to save menu changes.');
      // Reload so the UI cannot drift from what was actually stored.
      load();
    }
  };

  const handleToggle = async page => {
    const showInMenu = !page.showInMenu;

    setPages(previous =>
      previous.map(item =>
        item._id === page._id ? { ...item, showInMenu } : item,
      ),
    );

    await persist(page._id, { showInMenu });
  };

  /**
   * Move a page up or down within the menu.
   *
   * Ordering is rewritten as a dense 0..n sequence across all shown pages,
   * so repeated moves cannot leave gaps or ties that make the order look
   * arbitrary.
   */
  const handleMove = async (page, direction) => {
    const shown = sortForMenu(pages.filter(item => item.showInMenu));
    const index = shown.findIndex(item => item._id === page._id);
    const target = index + direction;

    if (index === -1 || target < 0 || target >= shown.length) {
      return;
    }

    const reordered = [...shown];
    [reordered[index], reordered[target]] = [
      reordered[target],
      reordered[index],
    ];

    const orders = new Map(
      reordered.map((item, position) => [item._id, position]),
    );

    setPages(previous =>
      sortForMenu(
        previous.map(item =>
          orders.has(item._id)
            ? { ...item, menuOrder: orders.get(item._id) }
            : item,
        ),
      ),
    );

    // Persist every page whose stored order differs from its new position,
    // not just the two that swapped.
    //
    // Pages default to `menuOrder: 0`, so a freshly built menu has them all
    // tied. The dense renumber above then moves several at once, and saving
    // only the swapped pair would leave the rest at 0 — the footer would come
    // back in a different order than the one just arranged here.
    const changed = reordered.filter(
      item => (item.menuOrder || 0) !== orders.get(item._id),
    );

    await Promise.all(
      changed.map(item =>
        persist(item._id, { menuOrder: orders.get(item._id) }),
      ),
    );
  };

  const shownCount = pages.filter(page => page.showInMenu).length;

  return (
    <AdminLayout>
      <CmsAdminNav />
      <div className="container">
        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-12">
            <h2>Menus</h2>
            <p>
              Published pages shown in the site footer. Only published pages
              appear here — trashing or unpublishing a page removes its link.
            </p>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {isLoading ? (
          <p>Loading pages…</p>
        ) : (
          <table className="table table-striped">
            <thead>
              <tr>
                <th style={{ width: '120px' }}>In menu</th>
                <th>Title</th>
                <th>Slug</th>
                <th style={{ width: '140px' }}>Order</th>
              </tr>
            </thead>
            <tbody>
              {pages.map(page => (
                <tr key={page._id}>
                  <td>
                    <div className="checkbox">
                      <label htmlFor={`menu-toggle-${page._id}`}>
                        <input
                          id={`menu-toggle-${page._id}`}
                          type="checkbox"
                          checked={Boolean(page.showInMenu)}
                          onChange={() => handleToggle(page)}
                        />
                        Show
                      </label>
                    </div>
                  </td>
                  <td>{page.title}</td>
                  <td>
                    <code>{page.slug}</code>
                  </td>
                  <td>
                    {page.showInMenu && (
                      <div className="btn-group">
                        <button
                          type="button"
                          className="btn btn-default btn-xs"
                          aria-label={`Move ${page.title} up`}
                          onClick={() => handleMove(page, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn-default btn-xs"
                          aria-label={`Move ${page.title} down`}
                          onClick={() => handleMove(page, 1)}
                        >
                          ↓
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {!pages.length && (
                <tr>
                  <td colSpan="4">
                    No published pages yet. Publish a page to add it to the
                    menu.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        <p className="help-block">
          {shownCount} page{shownCount === 1 ? '' : 's'} in the footer menu.
        </p>
      </div>
    </AdminLayout>
  );
}

AdminCmsMenus.propTypes = {};
